import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const { t } = useTranslation();
  const [isInitializing, setIsInitializing] = useState(false);
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      const parsedUser = JSON.parse(savedUser);
      // Ensure required arrays exist and map backend properties to frontend
      return {
        ...parsedUser,
        ideaViews: parsedUser.ideaViews || parsedUser.idea_views || 0,
        dateJoined: parsedUser.dateJoined || parsedUser.date_joined || parsedUser.created_at,
        bookmarkedIdeas: parsedUser.bookmarkedIdeas || [],
        likedIdeas: parsedUser.likedIdeas || [],
        dislikedIdeas: parsedUser.dislikedIdeas || [],
        viewedIdeas: parsedUser.viewedIdeas || []
      };
    }
    return null;
  });

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const savedUser = localStorage.getItem('user');
    const savedToken = localStorage.getItem('token');
    return savedUser && savedToken ? true : false;
  });

  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, [user]);

  // Handle Supabase auth state changes (for Google OAuth redirect)
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { supabase } = await import('../lib/supabase');
        
        // Check if user is already signed in on page load  
        const { data: { session } } = await supabase.auth.getSession();
        const token = localStorage.getItem('token');
        
        // Handle case where we have a token but no session (OAuth callback)
        if (token && !user && !session && !isInitializing) {
          setIsInitializing(true);
          try {
            // Try to get user data from backend using the stored token
            const apiUrl = process.env.NODE_ENV === 'production' 
              ? process.env.REACT_APP_API_URL_PRODUCTION 
              : (process.env.REACT_APP_API_URL || 'https://idealyze-backend.vercel.app');
            
            const response = await fetch(`${apiUrl}/api/auth/me`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (response.ok) {
              const userData = await response.json();
              localStorage.setItem('user', JSON.stringify(userData));
              setUser(userData);
              setIsAuthenticated(true);
            } else {
              // Token is invalid, clear it
              localStorage.removeItem('token');
              localStorage.removeItem('user');
            }
          } catch (error) {
            console.error('Error validating stored token:', error);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          } finally {
            setIsInitializing(false);
          }
        } else if (session && session.user && !user && !isInitializing) {
          setIsInitializing(true);
          try {
            // Call backend to ensure user exists in users table
            const apiUrl = process.env.NODE_ENV === 'production' 
              ? process.env.REACT_APP_API_URL_PRODUCTION 
              : (process.env.REACT_APP_API_URL || 'https://idealyze-backend.vercel.app');
            const response = await fetch(`${apiUrl}/api/auth/google-signin`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
              },
              body: JSON.stringify({
                id: session.user.id,
                email: session.user.email,
                name: session.user.user_metadata?.name || session.user.user_metadata?.full_name || session.user.email.split('@')[0]
              })
            });

            if (response.ok) {
              const userData = await response.json();
              const initializedUser = {
                ...userData.user,
                ideaViews: userData.user.ideaViews || userData.user.idea_views || 0,
                dateJoined: userData.user.dateJoined || userData.user.date_joined || userData.user.created_at,
                bookmarkedIdeas: userData.user.bookmarkedIdeas || [],
                likedIdeas: userData.user.likedIdeas || [],
                dislikedIdeas: userData.user.dislikedIdeas || [],
                viewedIdeas: userData.user.viewedIdeas || []
              };
              
              localStorage.setItem('token', session.access_token);
              localStorage.setItem('user', JSON.stringify(initializedUser));
              setUser(initializedUser);
              setIsAuthenticated(true);
            } else {
              // Fallback
              const initializedUser = {
                id: session.user.id,
                email: session.user.email,
                name: session.user.user_metadata?.name || session.user.user_metadata?.full_name || session.user.email.split('@')[0],
                ideaViews: 0,
                dateJoined: session.user.created_at,
                bookmarkedIdeas: [],
                likedIdeas: [],
                dislikedIdeas: [],
                viewedIdeas: [],
                credits: 5,
                tier: 'free'
              };
              
              localStorage.setItem('token', session.access_token);
              localStorage.setItem('user', JSON.stringify(initializedUser));
              setUser(initializedUser);
              setIsAuthenticated(true);
            }
          } catch (error) {
            console.error('Error syncing existing session with backend:', error);
            // Fallback
            const initializedUser = {
              id: session.user.id,
              email: session.user.email,
              name: session.user.user_metadata?.name || session.user.user_metadata?.full_name || session.user.email.split('@')[0],
              ideaViews: 0,
              dateJoined: session.user.created_at,
              bookmarkedIdeas: [],
              likedIdeas: [],
              dislikedIdeas: [],
              viewedIdeas: [],
              credits: 5,
              tier: 'free'
            };
            
            localStorage.setItem('token', session.access_token);
            localStorage.setItem('user', JSON.stringify(initializedUser));
            setUser(initializedUser);
            setIsAuthenticated(true);
          }
          setIsInitializing(false);
        }

        // Listen for auth changes (Google OAuth callback)
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
          if (event === 'SIGNED_IN' && session && !user && !isInitializing) {
            setIsInitializing(true);
            try {
              // Call backend to ensure user exists in users table
              const apiUrl = process.env.NODE_ENV === 'production' 
              ? process.env.REACT_APP_API_URL_PRODUCTION 
              : (process.env.REACT_APP_API_URL || 'https://idealyze-backend.vercel.app');
              const response = await fetch(`${apiUrl}/api/auth/google-signin`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${session.access_token}`
                },
                body: JSON.stringify({
                  id: session.user.id,
                  email: session.user.email,
                  name: session.user.user_metadata?.name || session.user.user_metadata?.full_name || session.user.email.split('@')[0]
                })
              });

              if (response.ok) {
                const userData = await response.json();
                const initializedUser = {
                  ...userData.user,
                  ideaViews: userData.user.ideaViews || userData.user.idea_views || 0,
                  dateJoined: userData.user.dateJoined || userData.user.date_joined || userData.user.created_at,
                  bookmarkedIdeas: userData.user.bookmarkedIdeas || [],
                  likedIdeas: userData.user.likedIdeas || [],
                  dislikedIdeas: userData.user.dislikedIdeas || [],
                  viewedIdeas: userData.user.viewedIdeas || []
                };
                
                localStorage.setItem('token', session.access_token);
                localStorage.setItem('user', JSON.stringify(initializedUser));
                setUser(initializedUser);
                setIsAuthenticated(true);
              } else {
                // Fallback if backend fails
                const initializedUser = {
                  id: session.user.id,
                  email: session.user.email,
                  name: session.user.user_metadata?.name || session.user.user_metadata?.full_name || session.user.email.split('@')[0],
                  ideaViews: 0,
                  dateJoined: session.user.created_at,
                  bookmarkedIdeas: [],
                  likedIdeas: [],
                  dislikedIdeas: [],
                  viewedIdeas: [],
                  credits: 5,
                  tier: 'free'
                };
                
                localStorage.setItem('token', session.access_token);
                localStorage.setItem('user', JSON.stringify(initializedUser));
                setUser(initializedUser);
                setIsAuthenticated(true);
              }
            } catch (error) {
              console.error('Error syncing Google user with backend:', error);
              // Fallback if API call fails
              const initializedUser = {
                id: session.user.id,
                email: session.user.email,
                name: session.user.user_metadata?.name || session.user.user_metadata?.full_name || session.user.email.split('@')[0],
                ideaViews: 0,
                dateJoined: session.user.created_at,
                bookmarkedIdeas: [],
                likedIdeas: [],
                dislikedIdeas: [],
                viewedIdeas: [],
                credits: 5,
                tier: 'free'
              };
              
              localStorage.setItem('token', session.access_token);
              localStorage.setItem('user', JSON.stringify(initializedUser));
              setUser(initializedUser);
              setIsAuthenticated(true);
            }
          } else if (event === 'SIGNED_OUT') {
            setUser(null);
            setIsAuthenticated(false);
            localStorage.removeItem('user');
            localStorage.removeItem('token');
          }
        });

        return () => subscription.unsubscribe();
      } catch (error) {
        console.error('Auth initialization error:', error);
      }
    };

    initializeAuth();
  }, []);


  // Periodic refresh of user data every 15 seconds
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const refreshUserData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          // Use existing /me endpoint instead of new stats endpoint
          const apiUrl = process.env.NODE_ENV === 'production' 
            ? process.env.REACT_APP_API_URL_PRODUCTION 
            : (process.env.REACT_APP_API_URL || 'https://idealyze-backend.vercel.app');
          const response = await fetch(`${apiUrl}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            
            // Update the existing user object with fresh data from database
            // Only update credits, ideaViews, and tier - don't touch bookmarks
            setUser(prevUser => ({
              ...prevUser,
              credits: data.user.credits,
              ideaViews: data.user.ideaViews || data.user.idea_views || 0,
              tier: data.user.tier
            }));
          }
        }
      } catch (error) {
        console.error('Failed to refresh user data:', error);
        // Keep existing user data if refresh fails
      }
    };

    // Set up interval to refresh user data every 15 seconds
    const refreshInterval = setInterval(refreshUserData, 15000);
    
    return () => {
      clearInterval(refreshInterval);
    };
  }, [isAuthenticated, user?.id]); // Only depend on auth status and user id

  const toggleBookmark = async (ideaId) => {
    if (!user) return;
    
    // Immediate UI feedback
    setUser(prevUser => ({
      ...prevUser,
      bookmarkedIdeas: (prevUser.bookmarkedIdeas || []).includes(ideaId)
        ? (prevUser.bookmarkedIdeas || []).filter(id => id !== ideaId)
        : [...(prevUser.bookmarkedIdeas || []), ideaId]
    }));
    
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Call backend API to save bookmark to database
        const { apiClient } = await import('../services/api');
        const response = await apiClient.toggleBookmark(ideaId, token);
        // Bookmark operation completed successfully
        
        // Update user state with returned interaction arrays if available
        if (response && response.interactions) {
          setUser(prevUser => ({
            ...prevUser,
            ...response.interactions
          }));
        }
        
        // Refresh bookmarks to update any listening components
        await refreshBookmarks();
        
        // Return the response so components can access it
        return response;
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      // Revert the optimistic update on error
      setUser(prevUser => ({
        ...prevUser,
        bookmarkedIdeas: (prevUser.bookmarkedIdeas || []).includes(ideaId)
          ? (prevUser.bookmarkedIdeas || []).filter(id => id !== ideaId)
          : [...(prevUser.bookmarkedIdeas || []), ideaId]
      }));
    }
  };

  const toggleLike = async (ideaId) => {
    if (!user) return;
    
    // Immediate UI feedback  
    setUser(prevUser => ({
      ...prevUser,
      likedIdeas: (prevUser.likedIdeas || []).includes(ideaId)
        ? (prevUser.likedIdeas || []).filter(id => id !== ideaId)
        : [...(prevUser.likedIdeas || []), ideaId],
      // Remove from disliked if currently disliked
      dislikedIdeas: (prevUser.dislikedIdeas || []).filter(id => id !== ideaId)
    }));
    
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Call backend API to save like to database
        const { apiClient } = await import('../services/api');
        const response = await apiClient.toggleLike(ideaId, token);
        // Like operation completed successfully
        
        // Update user state with returned interaction arrays if available
        if (response && response.interactions) {
          setUser(prevUser => ({
            ...prevUser,
            ...response.interactions
          }));
        }
        
        // Return the response so components can access updated counts
        return response;
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      // Revert the optimistic update on error
      setUser(prevUser => ({
        ...prevUser,
        likedIdeas: (prevUser.likedIdeas || []).includes(ideaId)
          ? (prevUser.likedIdeas || []).filter(id => id !== ideaId)
          : [...(prevUser.likedIdeas || []), ideaId],
        dislikedIdeas: (prevUser.dislikedIdeas || []).filter(id => id !== ideaId)
      }));
    }
  };

  const toggleDislike = async (ideaId) => {
    if (!user) return;
    
    // Immediate UI feedback
    setUser(prevUser => ({
      ...prevUser,
      dislikedIdeas: (prevUser.dislikedIdeas || []).includes(ideaId)
        ? (prevUser.dislikedIdeas || []).filter(id => id !== ideaId)
        : [...(prevUser.dislikedIdeas || []), ideaId],
      // Remove from liked if currently liked
      likedIdeas: (prevUser.likedIdeas || []).filter(id => id !== ideaId)
    }));
    
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Call backend API to save dislike to database
        const { apiClient } = await import('../services/api');
        const response = await apiClient.toggleDislike(ideaId, token);
        // Dislike operation completed successfully
        
        // Update user state with returned interaction arrays if available
        if (response && response.interactions) {
          setUser(prevUser => ({
            ...prevUser,
            ...response.interactions
          }));
        }
        
        // Return the response so components can access updated counts
        return response;
      }
    } catch (error) {
      console.error('Error toggling dislike:', error);
      // Revert the optimistic update on error
      setUser(prevUser => ({
        ...prevUser,
        dislikedIdeas: (prevUser.dislikedIdeas || []).includes(ideaId)
          ? (prevUser.dislikedIdeas || []).filter(id => id !== ideaId)
          : [...(prevUser.dislikedIdeas || []), ideaId],
        likedIdeas: (prevUser.likedIdeas || []).filter(id => id !== ideaId)
      }));
    }
  };

  const useCredit = () => {
    setUser(prevUser => ({
      ...prevUser,
      credits: Math.max(0, prevUser.credits - 1)
    }));
  };

  const viewIdea = async (ideaId) => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Call backend API to record view in database - THIS IS CRITICAL FOR DASHBOARD
        const { apiClient } = await import('../services/api');
        await apiClient.recordIdeaView(ideaId, token);
        console.log('View recorded in database for idea:', ideaId);
      }
      
      // Update local state for immediate UI feedback
      setUser(prevUser => {
        // Only increment if this idea hasn't been viewed before
        const viewedIdeas = prevUser.viewedIdeas || [];
        if (!viewedIdeas.includes(ideaId)) {
          return {
            ...prevUser,
            ideaViews: (prevUser.ideaViews || 0) + 1,
            viewedIdeas: [...viewedIdeas, ideaId]
          };
        }
        return prevUser;
      });
    } catch (error) {
      console.error('Error recording view:', error);
      // Still update local state even if API fails
      setUser(prevUser => {
        const viewedIdeas = prevUser.viewedIdeas || [];
        if (!viewedIdeas.includes(ideaId)) {
          return {
            ...prevUser,
            ideaViews: (prevUser.ideaViews || 0) + 1,
            viewedIdeas: [...viewedIdeas, ideaId]
          };
        }
        return prevUser;
      });
    }
  };

  const recordAIInteraction = async (ideaId, featureType) => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Call backend API to record AI interaction in database
        const { apiClient } = await import('../services/api');
        await apiClient.recordAIInteraction(ideaId, featureType, token);
        console.log('AI interaction recorded:', featureType, 'for idea:', ideaId);
      }
    } catch (error) {
      console.error('Error recording AI interaction:', error);
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const { apiClient } = await import('../services/api');
      const response = await apiClient.changePassword(currentPassword, newPassword, token);
      
      return response;
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  };

  const upgradeToPro = () => {
    setUser(prevUser => ({
      ...prevUser,
      tier: 'pro',
      credits: 999 // Unlimited credits for pro users
    }));
  };

  const refreshBookmarks = async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const { apiClient } = await import('../services/api');
        const bookmarkedIdeas = await apiClient.getBookmarks(token);
        // Trigger a custom event to notify components that bookmarks have changed
        window.dispatchEvent(new CustomEvent('bookmarksUpdated', { detail: bookmarkedIdeas }));
      }
    } catch (error) {
      console.error('Error refreshing bookmarks:', error);
    }
  };

  const canViewIdeaDetails = () => {
    if (!user) return false;
    return user.tier === 'pro' || user.tier === 'idealizer' || (user.ideaViews || 0) < 3;
  };

  const canUseBuilderStudio = () => {
    if (!user) return false;
    return user.tier === 'pro' || user.tier === 'idealizer' || (user.credits || 0) > 0;
  };

  const login = async (email, password) => {
    try {
      const apiUrl = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL_PRODUCTION 
        : (process.env.REACT_APP_API_URL || 'https://idealyze-backend.vercel.app');
      const response = await fetch(`${apiUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        let errorMessage = 'Login failed';
        
        // Handle rate limiting (429) and other non-JSON responses
        if (response.status === 429) {
          errorMessage = 'Too many login attempts. Please wait a few minutes and try again.';
        } else {
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } catch (parseError) {
            // If response is not JSON (like rate limit responses), use response text
            try {
              const errorText = await response.text();
              if (errorText.includes('Too many')) {
                errorMessage = 'Too many login attempts. Please wait a few minutes and try again.';
              } else {
                errorMessage = errorText || errorMessage;
              }
            } catch (textError) {
              console.error('Failed to parse error response:', textError);
            }
          }
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Store token and user data
      localStorage.setItem('token', data.token);
      
      // Initialize user with required arrays and map backend properties
      const initializedUser = {
        ...data.user,
        ideaViews: data.user.ideaViews || data.user.idea_views || 0,
        dateJoined: data.user.dateJoined || data.user.date_joined || data.user.created_at,
        bookmarkedIdeas: data.user.bookmarkedIdeas || [],
        likedIdeas: data.user.likedIdeas || [],
        dislikedIdeas: data.user.dislikedIdeas || [],
        viewedIdeas: data.user.viewedIdeas || []
      };
      
      localStorage.setItem('user', JSON.stringify(initializedUser));
      setUser(initializedUser);
      setIsAuthenticated(true);
      
      return data.user;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const signup = async (name, email, password) => {
    try {
      const apiUrl = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL_PRODUCTION 
        : (process.env.REACT_APP_API_URL || 'https://idealyze-backend.vercel.app');
      const response = await fetch(`${apiUrl}/api/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, password }),
      });

      if (!response.ok) {
        let errorMessage = 'Signup failed';
        
        // Handle rate limiting (429) and other non-JSON responses
        if (response.status === 429) {
          errorMessage = 'Too many signup attempts. Please wait a few minutes and try again.';
        } else {
          try {
            const errorData = await response.json();
            
            // Check if it's the RLS error but everything else worked
            if (errorData.error && errorData.error.includes('row-level security policy')) {
              // RLS error occurred but user was likely created successfully
              // Show user-friendly message instead of technical error
              throw new Error(t('auth.emailConfirmation'));
            }
            
            errorMessage = errorData.error || errorMessage;
          } catch (parseError) {
            // If response is not JSON (like rate limit responses), use response text
            try {
              const errorText = await response.text();
              if (errorText.includes('Too many')) {
                errorMessage = 'Too many signup attempts. Please wait a few minutes and try again.';
              } else {
                errorMessage = errorText || errorMessage;
              }
            } catch (textError) {
              console.error('Failed to parse error response:', textError);
            }
          }
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Handle different response scenarios
      if (data.token && data.user) {
        // User is automatically logged in
        localStorage.setItem('token', data.token);
        
        // Initialize user with required arrays and map backend properties
        const initializedUser = {
          ...data.user,
          ideaViews: data.user.ideaViews || data.user.idea_views || 0,
          dateJoined: data.user.dateJoined || data.user.date_joined || data.user.created_at,
          bookmarkedIdeas: data.user.bookmarkedIdeas || [],
          likedIdeas: data.user.likedIdeas || [],
          dislikedIdeas: data.user.dislikedIdeas || [],
          viewedIdeas: data.user.viewedIdeas || []
        };
        
        localStorage.setItem('user', JSON.stringify(initializedUser));
        setUser(initializedUser);
        setIsAuthenticated(true);
        return data.user;
      } else if (data.message) {
        // Email confirmation required
        throw new Error(data.message);
      } else {
        throw new Error('Unexpected response format');
      }
    } catch (error) {
      console.error('Signup error:', error);
      
      // If it's the RLS error, replace with user-friendly message
      if (error.message && error.message.includes('row-level security policy')) {
        throw new Error(t('auth.emailConfirmation'));
      }
      
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { supabase } = await import('../lib/supabase');
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth?callback=true`
        }
      });

      if (error) {
        throw new Error(error.message);
      }

      return data;
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Sign out from Supabase if user signed in via OAuth
      const { supabase } = await import('../lib/supabase');
      await supabase.auth.signOut();
    } catch (error) {
      console.log('Supabase signout error (not critical):', error);
    }

    // Always clear local state
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  const requestPasswordReset = async (email) => {
    try {
      const { supabase } = await import('../lib/supabase');
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?reset=true`
      });

      if (error) {
        throw new Error(error.message);
      }

      return { success: true, message: 'Password reset email sent successfully' };
    } catch (error) {
      console.error('Password reset request error:', error);
      throw error;
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      const { supabase } = await import('../lib/supabase');
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        throw new Error(error.message);
      }

      return { success: true, message: 'Password updated successfully' };
    } catch (error) {
      console.error('Password update error:', error);
      throw error;
    }
  };

  const value = {
    user,
    setUser,
    isAuthenticated,
    login,
    signup,
    signInWithGoogle,
    logout,
    requestPasswordReset,
    updatePassword,
    toggleBookmark,
    toggleLike,
    toggleDislike,
    useCredit,
    viewIdea,
    recordAIInteraction,
    changePassword,
    upgradeToPro,
    refreshBookmarks,
    canViewIdeaDetails,
    canUseBuilderStudio
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};